import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type ManagementAction } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill } from "@/components/dash/ui";

// Mirror of the backend VALID_TRANSITIONS so the UI only offers legal moves.
const TRANSITIONS: Record<string, string[]> = {
  open: ["acknowledged", "in_progress", "resolved", "dismissed"],
  acknowledged: ["in_progress", "resolved", "dismissed", "open"],
  in_progress: ["acknowledged", "resolved", "dismissed"],
  resolved: ["open"],
  dismissed: ["open"],
};

const FILTERS = ["active", "open", "acknowledged", "in_progress", "resolved", "dismissed", "all"];

function ActionEvents({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["action-events", id], queryFn: () => dashApi.decisionActionEvents(id) });
  if (q.isLoading) return <p className="px-3 py-2 text-xs text-slate-500">Loading history…</p>;
  if (!q.data?.events.length) return <p className="px-3 py-2 text-xs text-slate-500">No history.</p>;
  return (
    <ul className="space-y-1.5 px-3 py-2">
      {q.data.events.map((e) => (
        <li key={e.id} className="flex items-start gap-2 text-[11px] text-slate-400">
          <span className="text-slate-600">{e.ts ? fmtDateTime(e.ts) : ""}</span>
          <span className="text-slate-300">{e.event_type}</span>
          {e.from_status && <span>{e.from_status} → {e.to_status}</span>}
          {e.note && <span className="text-slate-500">“{e.note}”</span>}
          {e.actor_email && <span className="ml-auto text-slate-600">{e.actor_email}</span>}
        </li>
      ))}
    </ul>
  );
}

function ActionRow({ a, isAdmin }: { a: ManagementAction; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const mut = useMutation({
    mutationFn: (body: Parameters<typeof dashApi.updateAction>[1]) => dashApi.updateAction(a.id, body),
    onSuccess: () => qc.invalidateQueries(),
  });
  const next = TRANSITIONS[a.status] ?? [];
  return (
    <>
      <tr className="text-slate-300">
        <td className="py-2">
          <button type="button" className="text-left hover:text-brand-300" onClick={() => setOpen((o) => !o)}>
            {a.title}
          </button>
          <div className="text-[11px] text-slate-600">{a.category}{a.source_code ? ` · ${a.source_code}` : ""}</div>
        </td>
        <td className="py-2"><StatusPill status={a.severity} /></td>
        <td className="py-2"><StatusPill status={a.status} label={a.status} /></td>
        <td className="py-2 text-slate-400">{a.owner ?? "—"}</td>
        <td className="py-2 text-[11px] text-slate-500">{a.due_date ?? "—"}</td>
        <td className="py-2 text-[11px] text-slate-500">{fmtRelative(a.updated_at)}</td>
        <td className="py-2 text-right">
          {isAdmin && next.length > 0 && (
            <select
              value=""
              onChange={(e) => { if (e.target.value) mut.mutate({ status: e.target.value }); }}
              className="rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-[11px] text-slate-200 [color-scheme:dark]"
            >
              <option value="">Move to…</option>
              {next.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="bg-ink-800/40">
            <div className="space-y-2">
              {a.detail && <p className="px-3 pt-2 text-xs text-slate-400">{a.detail}</p>}
              {isAdmin && <OwnerDueEditor a={a} onSave={(b) => mut.mutate(b)} pending={mut.isPending} />}
              <ActionEvents id={a.id} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function OwnerDueEditor({ a, onSave, pending }: { a: ManagementAction; onSave: (b: { owner?: string; due_date?: string | null; clear_due?: boolean; note?: string }) => void; pending: boolean }) {
  const [owner, setOwner] = useState(a.owner ?? "");
  const [due, setDue] = useState(a.due_date ?? "");
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-wrap items-end gap-2 px-3">
      <label className="text-[11px] text-slate-500">Owner
        <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="unassigned"
          className="mt-0.5 block rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-xs text-slate-200" />
      </label>
      <label className="text-[11px] text-slate-500">Due
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
          className="mt-0.5 block rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-xs text-slate-200 [color-scheme:dark]" />
      </label>
      <label className="flex-1 text-[11px] text-slate-500">Note
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional note"
          className="mt-0.5 block w-full rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-xs text-slate-200" />
      </label>
      <button type="button" disabled={pending}
        onClick={() => onSave({
          owner,
          ...(due ? { due_date: due } : a.due_date ? { clear_due: true } : {}),
          note: note || undefined,
        })}
        className="rounded-md border border-brand-500/40 bg-brand-500/10 px-2.5 py-1 text-xs text-brand-200 hover:bg-brand-500/20 disabled:opacity-50">
        Save
      </button>
    </div>
  );
}

export default function ManagementActionsPage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [filter, setFilter] = useState("active");
  const q = useQuery({ queryKey: ["decisions-actions", filter], queryFn: () => dashApi.decisionsActions(filter === "all" ? undefined : filter) });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Management Actions"
        description="IRONDash-only follow-up on decisions and risks — status, owner, due date, notes and a full audit trail. These actions are internal metadata and never write back to any source system."
      />
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`rounded-md px-2.5 py-1 text-xs ${filter === f ? "bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40" : "bg-ink-800 text-slate-400 hover:bg-ink-700"}`}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>
      <Panel title={`Actions (${q.data?.actions.length ?? 0})`}>
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.actions.length ? (
          <EmptyState>No actions in this view. Track a priority from Today's Priorities to create one.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">Action</th>
                <th className="pb-2 font-medium">Severity</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium">Owner</th>
                <th className="pb-2 font-medium">Due</th>
                <th className="pb-2 font-medium">Updated</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {q.data.actions.map((a) => <ActionRow key={a.id} a={a} isAdmin={isAdmin} />)}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
